import React, { useState } from 'react';
import { ChevronRightIcon, BookmarkIcon, DollarSignIcon, InfoIcon, AlertTriangleIcon } from 'lucide-react';
export function DecimalPrecisionConfiguration({
  locale = 'en-US'
}) {
  const [decimalPrecision, setDecimalPrecision] = useState(2);
  const [scope, setScope] = useState('Tenant');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // This would come from an API in a real application
  const currentRoundingSettings = {
    mode: 'Nearest',
    precision: 0.01
  };
  const handlePrecisionChange = e => {
    setDecimalPrecision(parseInt(e.target.value, 10));
  };
  const handleScopeChange = e => {
    setScope(e.target.value);
  };
  const handleSaveClick = () => {
    setShowConfirmModal(true);
  };
  const handleConfirmSave = () => {
    // In a real application, this would make an API call to save the settings
    console.log('Saving decimal precision:', {
      precision: decimalPrecision,
      scope: scope
    });
    setShowConfirmModal(false);
    // Show success message
    alert('Decimal precision settings saved successfully');
  };
  const handleCancelSave = () => {
    setShowConfirmModal(false);
  };
  return <div data-id="element-14969">
      <div className="mb-8" data-id="element-14970">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-14971">
          Decimal Precision Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-14972">
          Configure the number of decimal places used for all currency fields
          across the application
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb" data-id="element-14973">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-14974">
          <li className="inline-flex items-center" data-id="element-14975">
            <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-14976">
              <BookmarkIcon className="w-4 h-4 mr-2" data-id="element-14977" />
              Settings
            </span>
          </li>
          <li data-id="element-14978">
            <div className="flex items-center" data-id="element-14979">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-14980" />
              <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-14981">
                <DollarSignIcon className="w-4 h-4 mr-2" data-id="element-14982" />
                Decimal Precision
              </span>
            </div>
          </li>
        </ol>
      </nav>
      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6" data-id="element-14983">
        <div className="flex" data-id="element-14984">
          <div className="flex-shrink-0" data-id="element-14985">
            <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-14986" />
          </div>
          <div className="ml-3" data-id="element-14987">
            <p className="text-sm text-yellow-700" data-id="element-14988">
              ⚠️ Changing this setting will affect every currency field across
              RapidP2P, including all modules, APIs, and integrations. This
              should only be changed by authorised financial administrators.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-14989">
        <div className="px-4 py-5 sm:px-6" data-id="element-14990">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14991">
            Precision Settings
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-14992">
            Define how many decimal places are used for currency values
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-14993">
          <dl data-id="element-14994">
            {/* Decimal Precision Setting */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-14995">
              <dt className="text-sm font-medium text-gray-500" data-id="element-14996">
                Decimal Precision
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-14997">
                <select id="decimalPrecision" name="decimalPrecision" value={decimalPrecision} onChange={handlePrecisionChange} className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md" data-id="element-14998">
                  <option value={2} data-id="element-14999">2 Decimal Places (Standard)</option>
                  <option value={4} data-id="element-15000">4 Decimal Places (High Precision)</option>
                </select>
                <p className="mt-1 text-sm text-gray-500" data-id="element-15001">
                  Decimal Precision defines how many decimal places are stored
                  and displayed.
                </p>
              </dd>
            </div>
            {/* Scope Selector */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-15002">
              <dt className="text-sm font-medium text-gray-500" data-id="element-15003">Scope</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-15004">
                <select id="scope" name="scope" value={scope} onChange={handleScopeChange} className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md" data-id="element-15005">
                  <option value="Tenant" data-id="element-15006">Tenant (Global Setting)</option>
                  <option value="Entity" data-id="element-15007">Entity</option>
                  <option value="Site" data-id="element-15008">Site</option>
                </select>
                <p className="mt-1 text-sm text-gray-500" data-id="element-15009">
                  Select the scope for this decimal precision setting.
                </p>
              </dd>
            </div>
            {/* Current Rounding Settings */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-15010">
              <dt className="text-sm font-medium text-gray-500" data-id="element-15011">
                Current Rounding Settings
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-15012">
                <div className="flex items-center" data-id="element-15013">
                  <span className="font-medium" data-id="element-15014">Mode:</span>
                  <span className="ml-2" data-id="element-15015">{currentRoundingSettings.mode}</span>
                  <span className="mx-3" data-id="element-15016">|</span>
                  <span className="font-medium" data-id="element-15017">Precision:</span>
                  <span className="ml-2" data-id="element-15018">
                    {currentRoundingSettings.precision}
                  </span>
                </div>
                <div className="mt-2 flex items-start" data-id="element-15019">
                  <InfoIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-15020" />
                  <p className="text-sm text-gray-500" data-id="element-15021">
                    Decimal Precision defines how many decimal places are stored
                    and displayed. Rounding settings determine how totals are
                    rounded for reporting and document output.
                  </p>
                </div>
              </dd>
            </div>
          </dl>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6" data-id="element-15022">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleSaveClick} data-id="element-15023">
            Save Changes
          </button>
        </div>
      </div>
      {/* Confirmation Modal */}
      {showConfirmModal && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-15024">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-15025">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-15026">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-15027"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-15028">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-15029">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-15030">
                <div className="sm:flex sm:items-start" data-id="element-15031">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-15032">
                    <AlertTriangleIcon className="h-6 w-6 text-yellow-600" data-id="element-15033" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-15034">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15035">
                      Confirm Decimal Precision Change
                    </h3>
                    <div className="mt-2" data-id="element-15036">
                      <p className="text-sm text-gray-500" data-id="element-15037">
                        This change will immediately affect all monetary values
                        across the application for future calculations and
                        displays. Historic data will not be recalculated. Do you
                        want to proceed?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-15038">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={handleConfirmSave} data-id="element-15039">
                  Proceed
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={handleCancelSave} data-id="element-15040">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}